import type { ActionDoc } from "@shared/models/action"
import { generateActionDoc } from "@shared/models/action"
import type { PatchModel } from "@shared/models/patch"
import type { Narrative } from "cc-frontend/lib/actions/create-narrative"

export type FindInMemory = (modelName: string, id: string) => unknown

export interface PreparedAction {
  name: string
  payload: any
  patches?: Array<PatchModel> | null
  undoPatches?: Array<PatchModel> | null
  narrative?: ((findFn: FindInMemory, userId: string) => Narrative | null) | null | undefined
}

export interface ActionDocBuildContext {
  actionId: string
  clientCreatedAt: string
  createPatchId: () => string
  currentDocumentHref: string
  currentUrl: string
  fbc: string | undefined
  fbp: string | undefined
  findInMemory: FindInMemory
  fullStorySessionUrl: string | null
  groupId: string | null | undefined
  secondsSinceLastClientAction: number
  sequenceNumber: number
  sessionId: string
  userId: string
}

export interface ActionDocBuildContextInput {
  actionId: string
  createPatchId: () => string
  currentDocumentHref: string
  currentUrl: string
  fbc: string | undefined
  fbp: string | undefined
  findInMemory: FindInMemory
  formatDate: (date: Date) => string
  fullStorySessionUrl: string | null
  groupId: string | null | undefined
  lastActionOccurredAt: Date
  now: Date
  sequenceNumber: number
  sessionId: string | null | undefined
  userId: string | null | undefined
}

export function buildActionDocContext(input: ActionDocBuildContextInput): ActionDocBuildContext {
  return {
    actionId: input.actionId,
    clientCreatedAt: input.formatDate(input.now),
    createPatchId: input.createPatchId,
    currentDocumentHref: input.currentDocumentHref,
    currentUrl: input.currentUrl,
    fbc: input.fbc,
    fbp: input.fbp,
    findInMemory: input.findInMemory,
    fullStorySessionUrl: input.fullStorySessionUrl,
    groupId: input.groupId,
    secondsSinceLastClientAction: Math.floor(
      (input.now.valueOf() - input.lastActionOccurredAt.valueOf()) / 1000
    ),
    sequenceNumber: input.sequenceNumber,
    sessionId: input.sessionId || "anonymous",
    userId: input.userId || "anonymous",
  }
}

export function buildActionDoc(
  preparedAction: PreparedAction,
  context: ActionDocBuildContext
): ActionDoc {
  const patches = timestampPatches(
    preparedAction.patches,
    context.actionId,
    context.clientCreatedAt,
    context.createPatchId
  )
  const narrative = preparedAction.narrative
    ? preparedAction.narrative(context.findInMemory, context.userId)
    : null

  if (narrative) {
    narrative.context = {
      ...narrative.context,
      currentUrl: context.currentUrl,
      fbp: context.fbp,
      fbc: context.fbc,
      groupId: context.groupId,
    }
    narrative.url = narrative.url || context.currentDocumentHref
  }

  const isUserFacing = patches.length > 0 && !preparedAction.name.includes("INTERMEDIATE")
  return generateActionDoc(
    context.actionId,
    preparedAction.name,
    patches,
    narrative,
    context.userId,
    context.sessionId,
    context.sequenceNumber,
    context.secondsSinceLastClientAction,
    context.clientCreatedAt,
    context.fullStorySessionUrl,
    isUserFacing
  )
}

function timestampPatches(
  patches: Array<PatchModel> | null | undefined,
  actionId: string,
  formattedNow: string,
  createPatchId: () => string
): Array<PatchModel> {
  return (patches ?? []).map((patch) => {
    patch.id = createPatchId()
    patch.actionId = actionId
    patch.timeAtCreation = formattedNow
    patch.revisionAtCreation = null
    return patch
  })
}
